import { Link } from 'react-router-dom';
import { CalendarDays, MapPin, Store, Clock } from 'lucide-react';
import ShareButton from './ShareButton';
import { useT } from '../i18n/I18nContext';

const FALLBACK_IMG = 'https://images.unsplash.com/photo-1521335629791-ce4aec67dd47?w=600';

function formatDate(d, locale) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString(locale, { day: '2-digit', month: 'short' });
  } catch {
    return d;
  }
}

function isToday(d) {
  if (!d) return false;
  const today = new Date().toISOString().split('T')[0];
  return d === today;
}

function isActive(ev) {
  if (!ev?.dal_giorno || !ev?.al_giorno) return false;
  const today = new Date().toISOString().split('T')[0];
  return ev.dal_giorno <= today && today <= ev.al_giorno;
}

/** Card showing a shop with an upcoming temporary location (event/market). */
export default function ShopEventCard({ event }) {
  const { t, lang } = useT();
  const locale = lang === 'en' ? 'en-GB' : 'it-IT';
  const active = isActive(event);

  return (
    <Link
      to={`/negozio/${event.shop_id}`}
      className="block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-slate-100 hover:border-blue-300 group"
      data-testid={`shop-event-card-${event.id}`}
    >
      <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
        <img
          src={event.shop_logo || FALLBACK_IMG}
          alt={event.shop_nome}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => { e.target.src = FALLBACK_IMG; }}
        />
        <span
          className={`absolute top-3 left-3 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold shadow-md ${
            active ? 'bg-green-500 text-white' : 'bg-purple-600 text-white'
          }`}
        >
          <CalendarDays className="w-3.5 h-3.5" />
          {active ? t('events.on_today') : `${formatDate(event.dal_giorno, locale)}${event.dal_giorno !== event.al_giorno ? ` → ${formatDate(event.al_giorno, locale)}` : ''}`}
        </span>
        {event.distanza != null && (
          <span className="absolute top-3 right-3 bg-white/95 text-slate-700 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {event.distanza < 1 ? `${Math.round(event.distanza * 1000)} m` : `${event.distanza.toFixed(1)} km`}
          </span>
        )}
        <div className="absolute bottom-2 right-2" onClick={(e) => e.preventDefault()}>
          <ShareButton
            title={event.shop_nome}
            url={`${window.location.origin}/negozio/${event.shop_id}`}
            testId={`event-${event.id}-share`}
            variant="icon"
          />
        </div>
      </div>

      <div className="p-3">
        <div className="flex items-center gap-1 text-xs text-purple-600 font-semibold uppercase tracking-wide mb-1">
          <Store className="w-3 h-3" />
          <span className="capitalize">{event.shop_business_category || ''}</span>
        </div>
        <h3 className="font-outfit font-semibold text-slate-800 truncate" data-testid="shop-event-name">
          {event.shop_nome}
        </h3>
        <p className="text-sm text-slate-600 flex items-start gap-1 mt-1">
          <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
          <span className="line-clamp-1">
            {event.indirizzo ? `${event.indirizzo}${event.citta ? `, ${event.citta}` : ''}` : event.citta || t('events.on_map')}
          </span>
        </p>
        <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
          <Clock className="w-3 h-3" />
          {event.dal_orario} – {event.al_orario}
          {isToday(event.dal_giorno) && !active && (
            <span className="ml-1 text-amber-600 font-medium">· {t('events.on_today')}</span>
          )}
        </p>
        {event.note && (
          <p className="text-xs text-slate-500 italic mt-1 truncate">{event.note}</p>
        )}
      </div>
    </Link>
  );
}
